/**
 * Pure helpers for the unified summary bar.
 *
 * The bar is split into two zones:
 *  - **time-off zone** (left): approved + pending time off, drawn in cyan
 *    (solid for approved, diagonal stripes for pending). Both reduce capacity.
 *  - **Work zone** (right): tracked + "left to track" (booked − tracked) +
 *    available (unallocated availability), with anything past the `Xh cap`
 *    line drawn in **purple** to flag overflow.
 *
 * Tracked time is split into a *regular* slice and a *background-work* slice.
 * BG work is rendered with the same color at lower opacity so the user still
 * sees how much was logged but recognises it as not counting toward the active
 * workload progress. The same split applies on both sides of the cap line.
 *
 * `capacity` is expected to already be the *effective* capacity (raw capacity
 * minus APPROVED time-off — see `effectiveCapacityHours` on the domain types).
 * Approved time-off is still rendered as its own bar segment for context, but
 * since it's already excluded from `capacity`, only PENDING time-off is
 * subtracted here when sizing the work zone — subtracting approved again
 * would double-count it.
 *
 * `buildBarSegments` returns segment sizes (in hours, kept as raw numbers so
 * flex-grow can use them directly). `getTrackedStateColor` returns the
 * trafficlight color used for the tracked-and-left-to-track segments.
 */
import { getProgressColor } from '../../helpers/progress-color.helper';
import { calculateProgressPercentage } from '../../helpers/progress-percentage.helper';

/** Which of the two rounded tracks renders the capacity divider line. */
export enum CapLineZone {
  /** Cap line sits inside the time-off bar — happens when time-off alone overruns capacity. */
  TimeOff = 'timeoff',
  /** Cap line sits inside the work bar at the regular/over boundary (regular case). */
  Work = 'work',
}

export interface BarSegments {
  timeOffApproved: number;
  timeOffPending: number;
  /** Regular tracked portion below the capacity line. */
  trackedRegularUnder: number;
  /** Background-work tracked portion below the capacity line. */
  trackedBackgroundUnder: number;
  /** Regular tracked portion above the capacity line. */
  trackedRegularOver: number;
  /** Background-work tracked portion above the capacity line. */
  trackedBackgroundOver: number;
  /** "Left to track" portion below the capacity line (regular + BG merged). */
  todoUnder: number;
  /** "Left to track" portion above the capacity line (regular + BG merged). */
  todoOver: number;
  /** Unallocated availability. */
  available: number;
  /** Total amount in hours that exceeds capacity. */
  over: number;
  /** Position (0..100) of the capacity divider within the host zone bar. */
  capLinePos: number;
  /**
   * Which zone hosts the cap line. When pending time-off alone exceeds the
   * effective capacity the line sits inside the time-off bar at
   * `(approved + cap) / timeOffTotal * 100%`; otherwise it sits inside the
   * work bar at the regular/over boundary.
   */
  capLineZone: CapLineZone;
  /** Whether to show the capacity line. */
  showCapLine: boolean;
}

interface BuildSegmentsInput {
  tracked: number;
  /** Subset of `tracked` that was logged against background-work assignments. */
  trackedBackground: number;
  booked: number;
  /** Effective capacity — raw capacity minus APPROVED time-off. */
  effectiveCapacity: number;
  timeOffApproved: number;
  timeOffPending: number;
}

/**
 * Trafficlight color for the tracked segment based on how full the effective
 * work capacity is (`workCapacity` is expected to already exclude approved
 * time-off — see `capacity` on `BuildSegmentsInput`).
 *
 * Uses the shared `getProgressColor` interpolated scale (red → yellow → green →
 * purple). Overflow renders as a separate purple segment, so we clamp the
 * percentage at 100 here to keep the tracked segment from going purple before
 * the cap line.
 */
export const getTrackedStateColor = (tracked: number, workCapacity: number): string => {
  const percentage = calculateProgressPercentage(tracked, workCapacity);
  return getProgressColor(Math.min(percentage, 100));
};

export const buildBarSegments = ({
  tracked,
  trackedBackground,
  booked,
  effectiveCapacity,
  timeOffApproved,
  timeOffPending,
}: BuildSegmentsInput): BarSegments => {
  const tr = Math.max(0, tracked);
  const trBg = Math.max(0, Math.min(trackedBackground, tr));
  const trReg = Math.max(0, tr - trBg);
  const todo = Math.max(0, booked - tr);
  const approvedTimeOff = Math.max(0, timeOffApproved);
  const pendingTimeOff = Math.max(0, timeOffPending);
  // Full time-off zone width (both statuses) — used only for the visual zone
  // sizing below, not for the cap/available/over math (see doc comment).
  const timeOffTotal = approvedTimeOff + pendingTimeOff;

  const cap = Math.max(0, effectiveCapacity);
  // Approved time-off is already excluded from `cap`, so only pending
  // competes with tracked/todo for the remaining effective capacity.
  const used = pendingTimeOff + tr + todo;

  const available = Math.max(0, cap - used);
  const over = Math.max(0, used - cap);

  // Room under the cap line that the work zone owns (after pending time-off).
  const workRoom = Math.max(0, cap - pendingTimeOff);

  // Regular tracked fills first; BG tracked sits immediately after.
  const trackedRegularUnder = Math.min(trReg, workRoom);
  const trackedRegularOver = Math.max(0, trReg - trackedRegularUnder);

  const roomAfterRegular = Math.max(0, workRoom - trackedRegularUnder);
  const trackedBackgroundUnder = Math.min(trBg, roomAfterRegular);
  const trackedBackgroundOver = Math.max(0, trBg - trackedBackgroundUnder);

  const roomAfterTracked = Math.max(0, roomAfterRegular - trackedBackgroundUnder);
  const todoUnder = Math.min(todo, roomAfterTracked);
  const todoOver = Math.max(0, todo - todoUnder);

  // The cap line sits at the boundary where the work bar transitions from
  // under-cap to over-cap. Compute purely from segment sizes so the line is
  // always glued to that boundary regardless of which buckets the over-cap
  // spills into. NOTE: `over` is the total of the three "Over" segments — it
  // must NOT be added on top of them, or the denominator gets doubled and the
  // line drifts inward.
  const workUnder = trackedRegularUnder + trackedBackgroundUnder + todoUnder + available;
  const workOver = trackedRegularOver + trackedBackgroundOver + todoOver;
  const workTotal = workUnder + workOver;

  // When pending time-off alone overruns the effective capacity (approved is
  // already excluded from `cap`) the over-segments all sit inside the
  // time-off zone; the cap line must live in the time-off bar. Its position
  // there is `(approved + cap) / timeOffTotal` — i.e. the raw-capacity
  // boundary expressed as a fraction of the full (approved + pending) zone.
  // Otherwise the cap line stays in the work bar at the under-cap/over-cap
  // boundary.
  const capInTimeOff = cap > 0 && pendingTimeOff > cap;
  const capLineZone: CapLineZone = capInTimeOff ? CapLineZone.TimeOff : CapLineZone.Work;
  const capLinePos = capInTimeOff
    ? Math.min(100, ((approvedTimeOff + cap) / timeOffTotal) * 100)
    : workTotal > 0
      ? Math.min(100, (workUnder / workTotal) * 100)
      : 0;
  const showCapLine = over > 0 && cap > 0;

  return {
    timeOffApproved: Math.max(0, timeOffApproved),
    timeOffPending: Math.max(0, timeOffPending),
    trackedRegularUnder,
    trackedBackgroundUnder,
    trackedRegularOver,
    trackedBackgroundOver,
    todoUnder,
    todoOver,
    available,
    over,
    capLinePos,
    capLineZone,
    showCapLine,
  };
};
